<script>
import { getRandomId } from '../../utils/helpers';
import { findRefsInVNode, disablePageScrollWhileSwipingIOS } from './helpers';

import { playSound } from '../../services/sound';

const getFlickity = () => import(/* webpackPrefetch: true */ 'flickity');

const flickityDefaultOptions = {
  // wrapAround: true,
  prevNextButtons: false,
};

export default {
  name: 'SliderController',

  props: {
    el: {
      type: String,
      default: undefined,
    },

    childsAreSlides: {
      type: Boolean,
      default: true,
    },

    renderless: {
      type: Boolean,
      default: false,
    },

    activeSlideIndex: {
      type: Number,
      default: 0,
    },

    // will be merged with flickity options
    options: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      flickity: null,
      flickityInstance: null,
      progress: null,
      randomId: getRandomId(),
    };
  },

  computed: {
    flickityOptions() {
      return {
        ...flickityDefaultOptions,
        ...this.options,
      };
    },

    isProvidingSliderProps() {
      return this.renderless || !this.childsAreSlides;
    },

    sliderId() {
      return `slider__${this.randomId}`;
    },
  },

  watch: {
    'flickityInstance.selectedIndex': function(selectedIndex) {
      playSound('fxA', 'swipe');

      this.$emit('switchState', selectedIndex);
    },
    activeSlideIndex(slideIndex) {
      if (this.flickityInstance) this.flickityInstance.select(slideIndex);
    },
  },

  created() {
    // start loading as early as possible
    this.flickity = getFlickity();
  },

  async mounted() {
    const el = this.isProvidingSliderProps ? await this.findSliderRefInSlot() : this.$el;

    if (!el) {
      // eslint-disable-next-line no-console
      // console.log('no el found');
      return;
    }

    await this.initFlickity(el);
    this.initListener();

    disablePageScrollWhileSwipingIOS(el);
  },

  methods: {
    async findSliderRefInSlot() {
      // wait for children to mount
      await this.$nextTick();
      const foundEls = await findRefsInVNode(
        [this.sliderId],
        // we start the search with parent because we assume the ref is
        // in the default slot which is the parent context
        this.$parent
      );

      return foundEls[this.sliderId];
    },

    async initFlickity(el) {
      const { default: flickity } = await this.flickity;
      this.flickityInstance = new flickity(el, this.flickityOptions);
      this.$once('beforeDestroy', () => {
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      });

      this.$emit('init', this.flickityInstance);
    },

    initListener() {
      // TODO: make parallax scroll effect with this https://flickity.metafizzy.co/events.html#scroll
      const setProgress = progress => {
        this.progress = progress;
      };
      this.flickityInstance.on('scroll', setProgress);
      this.$once('beforeDestroy', this.flickityInstance.off('scroll', setProgress));
    },
  },

  render(createElement) {
    const defaultSlotBindings = {
      progress: this.progress,
      ...(this.flickityInstance || {}),
    };

    if (this.isProvidingSliderProps) {
      defaultSlotBindings.sliderProps = { ref: this.sliderId, class: 'slider-controller' };
    }

    return this.renderless
      ? this.$scopedSlots.default(defaultSlotBindings)
      : createElement(
          this.el || 'div',
          { class: 'slider-controller' },
          this.$scopedSlots.default(defaultSlotBindings)
        );
  },
};
</script>

<!-- TODO: add scoped styles -->
<style src="flickity/dist/flickity.css"></style>
<style lang="scss" scoped>
// style in child elements with scoped enabled
::v-deep.slider-controller,
::v-deep .slider-controller {
  padding-bottom: 20px;
}

// position dots in children
::v-deep {
  .flickity-page-dots {
    bottom: 0;
  }

  // dots are lines
  .flickity-page-dots .dot {
    height: 4px;
    margin: 0;
    border-radius: 2px;
    width: 14px;
    background-color: $color-white;
    transition: opacity 0.3s ease;

    & + .dot {
      margin-left: 5px;
    }
  }
}
</style>
